import React, { useState, useEffect } from 'react';
import { Star, Check, X, Trash2, MessageSquare, Plus, Edit, Award, EyeOff, Eye, Globe, Image, Mail, Phone } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';

const AdminReviews = () => {
  const { fetchWithAuth } = useAdminAuth();
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [filter, setFilter] = useState({ is_approved: '', rating: '' });
  const [showModal, setShowModal] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [formData, setFormData] = useState({
    author_name: '',
    product_id: '',
    rating: 5,
    comment: '',
    is_featured: false,
    is_approved: true
  });

  useEffect(() => { loadReviews(); loadProducts(); }, [pagination.page, filter]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: pagination.page, limit: 20 });
      if (filter.is_approved !== '') params.append('is_approved', filter.is_approved);
      if (filter.rating) params.append('rating', filter.rating);
      const res = await fetchWithAuth(`/api/admin/reviews?${params}`);
      if (res.ok) {
        const data = await res.json();
        setReviews(data.reviews);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const loadProducts = async () => {
    try {
      const res = await fetchWithAuth('/api/admin/products?limit=100');
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products || []);
      }
    } catch (error) { console.error(error); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = editingReview
        ? await fetchWithAuth(`/api/admin/reviews/${editingReview.id}`, { method: 'PUT', body: formData })
        : await fetchWithAuth('/api/admin/reviews', { method: 'POST', body: formData });
      
      if (res.ok) {
        toast.success(editingReview ? 'Reseña actualizada' : 'Reseña creada');
        setShowModal(false);
        setEditingReview(null);
        resetForm();
        loadReviews();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error');
      }
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const resetForm = () => {
    setFormData({
      author_name: '',
      product_id: '',
      rating: 5,
      comment: '',
      is_featured: false,
      is_approved: true
    });
  };

  const openEditModal = (review) => {
    setEditingReview(review);
    setFormData({
      author_name: review.author_name || `${review.user?.first_name || ''} ${review.user?.last_name || ''}`,
      product_id: review.product_id || '',
      rating: review.rating,
      comment: review.comment || '',
      is_featured: review.is_featured || false,
      is_approved: review.is_approved || false
    });
    setShowModal(true);
  };

  const approveReview = async (id) => {
    try {
      const res = await fetchWithAuth(`/api/admin/reviews/${id}/approve`, { method: 'POST' });
      if (res.ok) { toast.success('Reseña aprobada'); loadReviews(); }
    } catch (error) { toast.error('Error'); }
  };

  const rejectReview = async (id) => {
    try {
      const res = await fetchWithAuth(`/api/admin/reviews/${id}/reject`, { method: 'POST' });
      if (res.ok) { toast.success('Reseña rechazada'); loadReviews(); }
    } catch (error) { toast.error('Error'); }
  };

  const toggleFeatured = async (id) => {
    try {
      const res = await fetchWithAuth(`/api/admin/reviews/${id}/feature`, { method: 'POST' });
      if (res.ok) { 
        const data = await res.json();
        toast.success(data.is_featured ? 'Reseña destacada' : 'Reseña ya no destacada'); 
        loadReviews(); 
      }
    } catch (error) { toast.error('Error'); }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('¿Eliminar esta reseña permanentemente?')) return;
    try {
      const res = await fetchWithAuth(`/api/admin/reviews/${id}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Reseña eliminada'); loadReviews(); }
    } catch (error) { toast.error('Error'); }
  };

  const toggleHidden = async (id, currentStatus) => {
    try {
      const res = await fetchWithAuth(`/api/admin/reviews/${id}/hide`, { method: 'POST' });
      if (res.ok) { 
        const data = await res.json();
        toast.success(data.message); 
        loadReviews(); 
      }
    } catch (error) { toast.error('Error'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-MX') : '-';

  const renderStars = (rating, interactive = false, onChange = null) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star 
          key={i} 
          size={interactive ? 24 : 14} 
          className={`${i <= rating ? 'text-amber-500 fill-amber-500' : 'text-gray-600'} ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
          onClick={interactive ? () => onChange(i) : undefined}
        />
      ))}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-reviews">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-white">Reseñas</h2>
          <p className="text-gray-400 text-sm">{pagination.total} reseñas</p>
        </div>
        <button 
          onClick={() => { resetForm(); setEditingReview(null); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg"
          data-testid="new-review-btn"
        >
          <Plus size={18} /> Nueva Reseña
        </button>
      </div>

      {/* Filters */}
      <div className="bg-gray-800 rounded-xl p-4">
        <div className="flex gap-4">
          <select value={filter.is_approved} onChange={e => setFilter(prev => ({ ...prev, is_approved: e.target.value }))} className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white">
            <option value="">Todos los estados</option>
            <option value="true">Aprobadas</option>
            <option value="false">Pendientes/Rechazadas</option>
          </select>
          <select value={filter.rating} onChange={e => setFilter(prev => ({ ...prev, rating: e.target.value }))} className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white">
            <option value="">Todas las calificaciones</option>
            {[5, 4, 3, 2, 1].map(r => <option key={r} value={r}>{r} estrellas</option>)}
          </select>
        </div>
      </div>

      {/* Reviews */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div></div>
        ) : reviews.length === 0 ? (
          <div className="bg-gray-800 rounded-xl p-12 text-center text-gray-400">
            <MessageSquare className="mx-auto mb-3" size={40} />
            <p>No hay reseñas</p>
            <p className="text-sm mt-2">Crea tu primera reseña destacada</p>
          </div>
        ) : reviews.map(review => (
          <div key={review.id} className={`bg-gray-800 rounded-xl p-6 ${review.is_featured ? 'ring-2 ring-amber-500/50' : ''} ${review.is_hidden ? 'opacity-50' : ''}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  {renderStars(review.rating)}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${review.is_approved ? 'bg-green-500/10 text-green-400' : 'bg-yellow-500/10 text-yellow-400'}`}>
                    {review.is_approved ? 'Aprobada' : 'Pendiente'}
                  </span>
                  {review.is_featured && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 flex items-center gap-1">
                      <Award size={12} /> Destacada
                    </span>
                  )}
                  {review.is_hidden && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 flex items-center gap-1">
                      <EyeOff size={12} /> Oculta
                    </span>
                  )}
                  {review.source === 'website' && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-neon-cyan/10 text-neon-cyan flex items-center gap-1">
                      <Globe size={12} /> Web
                    </span>
                  )}
                </div>
                <p className="text-white mb-2">"{review.comment || 'Sin comentario'}"</p>
                
                {/* Review Photos */}
                {review.photos && review.photos.length > 0 && (
                  <div className="flex gap-2 mb-3 flex-wrap">
                    {review.photos.map((photo, idx) => (
                      <a 
                        key={idx} 
                        href={photo} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img 
                          src={photo} 
                          alt={`Foto ${idx + 1}`}
                          className="w-16 h-16 object-cover rounded-lg border border-gray-600 hover:border-neon-cyan transition-colors"
                        />
                      </a>
                    ))}
                    <span className="text-xs text-gray-500 self-end">
                      <Image size={12} className="inline mr-1" />
                      {review.photos.length} foto{review.photos.length > 1 ? 's' : ''}
                    </span>
                  </div>
                )}
                
                <div className="flex items-center gap-4 text-sm text-gray-400 flex-wrap">
                  <span>Por: {review.author_name || `${review.user?.first_name || ''} ${review.user?.last_name || ''}`}</span>
                  {review.author_email && (
                    <>
                      <span>•</span>
                      <span className="flex items-center gap-1 text-neon-cyan">
                        <Mail size={12} />
                        {review.author_email}
                      </span>
                    </>
                  )}
                  {review.author_phone && (
                    <>
                      <span>•</span>
                      <span className="flex items-center gap-1 text-neon-pink">
                        <Phone size={12} />
                        {review.author_phone}
                      </span>
                    </>
                  )}
                  {review.product && (
                    <>
                      <span>•</span>
                      <span>Producto: {review.product?.name || review.product?.product_code}</span>
                    </>
                  )}
                  <span>•</span>
                  <span>{formatDate(review.created_at)}</span>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button 
                  onClick={() => toggleFeatured(review.id)} 
                  className={`p-2 rounded-lg ${review.is_featured ? 'bg-amber-500/20 text-amber-400' : 'bg-gray-700 text-gray-400 hover:text-amber-400'}`} 
                  title={review.is_featured ? 'Quitar destacado' : 'Destacar'}
                >
                  <Award size={18} />
                </button>
                <button 
                  onClick={() => toggleHidden(review.id, review.is_hidden)} 
                  className={`p-2 rounded-lg ${review.is_hidden ? 'bg-red-500/20 text-red-400' : 'bg-gray-700 text-gray-400 hover:text-yellow-400'}`} 
                  title={review.is_hidden ? 'Mostrar' : 'Ocultar'}
                >
                  {review.is_hidden ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
                {!review.is_approved && (
                  <button onClick={() => approveReview(review.id)} className="p-2 bg-green-500/10 text-green-400 hover:bg-green-500/20 rounded-lg" title="Aprobar">
                    <Check size={18} />
                  </button>
                )}
                {review.is_approved && (
                  <button onClick={() => rejectReview(review.id)} className="p-2 bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 rounded-lg" title="Rechazar">
                    <X size={18} />
                  </button>
                )}
                <button onClick={() => openEditModal(review)} className="p-2 bg-gray-700 text-gray-400 hover:text-white rounded-lg" title="Editar">
                  <Edit size={18} />
                </button>
                <button onClick={() => deleteReview(review.id)} className="p-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-lg" title="Eliminar">
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <button onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} disabled={pagination.page === 1} className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50">Anterior</button>
          <span className="text-gray-400">Página {pagination.page} de {pagination.pages}</span>
          <button onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} disabled={pagination.page === pagination.pages} className="px-4 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50">Siguiente</button>
        </div>
      )}

      {/* Modal Crear/Editar Reseña */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowModal(false)}>
          <div className="bg-gray-800 rounded-xl max-w-lg w-full my-8 flex-shrink-0" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h3 className="text-xl font-semibold text-white">
                {editingReview ? 'Editar Reseña' : 'Nueva Reseña'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              {/* Author Name */}
              <div>
                <label className="block text-sm text-gray-300 mb-2">Nombre del Cliente *</label>
                <input
                  type="text"
                  value={formData.author_name}
                  onChange={e => setFormData(prev => ({ ...prev, author_name: e.target.value }))}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500"
                  placeholder="Ej: María García"
                  required
                />
              </div>

              {/* Product */}
              <div>
                <label className="block text-sm text-gray-300 mb-2">Producto (opcional)</label>
                <select
                  value={formData.product_id}
                  onChange={e => setFormData(prev => ({ ...prev, product_id: e.target.value }))}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500"
                >
                  <option value="">Sin producto específico</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.product_code})</option>
                  ))}
                </select>
              </div>

              {/* Rating */}
              <div>
                <label className="block text-sm text-gray-300 mb-2">Calificación *</label>
                <div className="flex items-center gap-4">
                  {renderStars(formData.rating, true, (r) => setFormData(prev => ({ ...prev, rating: r })))}
                  <span className="text-gray-400">{formData.rating} de 5</span>
                </div>
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm text-gray-300 mb-2">Comentario *</label>
                <textarea
                  value={formData.comment}
                  onChange={e => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500 min-h-[100px]"
                  placeholder="Escribe el comentario de la reseña..."
                  required
                />
              </div>

              {/* Featured & Approved */}
              <div className="flex gap-4">
                <label className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg cursor-pointer flex-1">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={e => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                    className="w-5 h-5 accent-amber-500"
                  />
                  <div>
                    <span className="text-sm text-white font-medium">Destacada</span>
                    <p className="text-xs text-gray-400">Aparecerá primero</p>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg cursor-pointer flex-1">
                  <input
                    type="checkbox"
                    checked={formData.is_approved}
                    onChange={e => setFormData(prev => ({ ...prev, is_approved: e.target.checked }))}
                    className="w-5 h-5 accent-amber-500"
                  />
                  <div>
                    <span className="text-sm text-white font-medium">Aprobada</span>
                    <p className="text-xs text-gray-400">Visible en el sitio</p>
                  </div>
                </label>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600">
                  Cancelar
                </button>
                <button type="submit" className="px-6 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-600">
                  {editingReview ? 'Guardar Cambios' : 'Crear Reseña'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminReviews;
